import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Row } from 'reactstrap';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { ReactComponent as ArrowDownIcon } from 'assets/icon-arrow-down.svg';
import { ReactComponent as ArrowUpIcon } from 'assets/icon-arrow-up.svg';
import { ReactComponent as TrashIcon } from 'assets/icon-trash.svg';
import { ReactComponent as ConfigurationIcon } from 'assets/icon-configuration.svg';
import { ButtonIcon } from 'shared/components/Buttons';
import { TextField, Switch } from 'shared/components/form/Inputs';
import DropdownSearch from 'shared/components/DropdownSearch';
import MultipleSelectSearch from 'shared/components/MultipleSelectSearch';
import TextArea from 'shared/components/textarea/TextArea';
import InputDate from 'shared/components/inputdate/InputDate';

import { updateCustomFieldList } from 'redux/actions/customFieldActions';

import { CustomFieldProps } from 'shared/prop-types/ReducerProps';
import FieldType from 'shared/helpers/fieldType';

import { CustomFieldLabel, GroupFieldWrapper } from './styles';

const GroupFieldComponent = ({ groupField, onRemove, onClickUpdate }) => {
  const dispatch = useDispatch();
  const { selectedDefaultLanguage } = useSelector((state) => state.topBar);
  const customField = useSelector((state) => state.customField);
  const theme = useSelector((state) => state.theme);
  const [sortCustomFields, setSortCustomFields] = useState([]);
  const { mainColor } = theme;

  useEffect(() => {
    setSortCustomFields(
      customField.customFields
        .filter((currentCustomField) => currentCustomField.parentId === groupField.id)
        .sort((a, b) => a.order - b.order)
    );
  }, [customField.customFields]);

  const getNameSelectedLanguage = (i18List) => {
    if (i18List) {
      const findName = i18List.find((x) => x.i18Code === selectedDefaultLanguage);
      if (findName && findName?.i18Name !== '') {
        return findName?.i18Name;
      }
      return i18List[0].i18Name;
    }
    return null;
  };

  const selectedMultipleList = (members) => {
    let newMembersMultiple = [];
    members
      .filter((item) => item.type !== FieldType.GROUP && item.id)
      .forEach((member) => {
        newMembersMultiple = [
          ...newMembersMultiple,
          { name: getNameSelectedLanguage(member.i18List), id: member.id },
        ];
      });
    return newMembersMultiple;
  };

  const getFieldByType = (field) => {
    const fields = new Map([
      [FieldType.TEXT, <TextField />],
      [FieldType.TEXT_AREA, <TextArea onChange={() => null} />],
      [FieldType.NUMBER, <TextField type="number" />],
      [FieldType.CURRENCY, <TextField type="number" />],
      [
        FieldType.LIST,
        <DropdownSearch
          id={`group-field-dropdown-search-${field.id}`}
          className="list-field"
          options={
            field.members
              ? [...field.members]
                  .sort((memberA, memberB) => memberA.order - memberB.order)
                  .map((member) => ({
                    name: getNameSelectedLanguage(member.i18List),
                    value: member.id,
                  }))
              : []
          }
        />,
      ],
      [
        FieldType.MULTI_LIST,
        <MultipleSelectSearch
          className="list-field"
          dataSource={selectedMultipleList(
            field.members
              ? [...field.members].sort((memberA, memberB) => memberA.order - memberB.order)
              : []
          )}
          fields={{ text: 'name', value: 'id' }}
        />,
      ],
      [FieldType.OPTION, <Switch id={field.id} color={mainColor} onChange={() => null} />],
      [FieldType.CALENDAR, <InputDate className="input-date" />],
    ]);

    return field.type ? fields.get(field.type) ?? null : null;
  };

  const onSort = (item, fromIndex, toIndex, animateClass) => {
    if (toIndex < 0) {
      return;
    }

    if (toIndex > sortCustomFields.length) {
      return;
    }
    const modifieCustomFields = sortCustomFields.slice();
    modifieCustomFields.splice(toIndex, 0, modifieCustomFields.splice(fromIndex, 1)[0]);
    const newSortCustomFields = modifieCustomFields.map((currentField) =>
      currentField.id === item.id ? { ...currentField, animateClass } : currentField
    );
    setSortCustomFields(newSortCustomFields);
    // Clear animation
    setTimeout(() => {
      // Update reducer custom field
      const customFieldsToUpdate = newSortCustomFields.map(
        ({ animateClass: animateClassField, ...rest }, index) => ({
          ...rest,
          order: index,
          updated: true,
        })
      );
      dispatch(updateCustomFieldList(customFieldsToUpdate));
    }, 800);
  };

  return (
    <GroupFieldWrapper>
      {sortCustomFields.map((field, index) => (
        <React.Fragment key={field.id}>
          <Row className={classNames(['row-field', field.animateClass])}>
            <Col className="custom-col-field" sm={11}>
              <div className="form__form-group">
                <CustomFieldLabel>{getNameSelectedLanguage(field.i18List)}</CustomFieldLabel>
                {getFieldByType(field)}
              </div>
            </Col>
            <Col
              className="d-flex flex-row justify-content-between align-items-center remove-padding-horizontal"
              sm={1}
            >
              <ButtonIcon
                className="action-icon"
                Icon={() => <ArrowDownIcon />}
                onClick={() => onSort(field, index, index + 1, 'animate-down')}
              />
              <ButtonIcon
                className="action-icon"
                Icon={() => <ArrowUpIcon />}
                onClick={() => onSort(field, index, index - 1, 'animate-up')}
              />
              <ButtonIcon
                className="action-icon"
                Icon={() => <TrashIcon />}
                onClick={() => onRemove(field)}
              />
              <ButtonIcon
                className="action-icon"
                Icon={() => <ConfigurationIcon />}
                onClick={() => onClickUpdate(field)}
              />
            </Col>
          </Row>
        </React.Fragment>
      ))}
    </GroupFieldWrapper>
  );
};

GroupFieldComponent.propTypes = {
  groupField: CustomFieldProps.isRequired,
  onRemove: PropTypes.func.isRequired,
  onClickUpdate: PropTypes.func.isRequired,
};

export default GroupFieldComponent;
